import { createFileRoute } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";

import Container from "@/components/containers/Container";
import Button from "@/components/forms/Button";
import Table from "@/components/table/Table";
import TableCell from "@/components/table/TableCell";
import TableHead from "@/components/table/TableHead";
import TableHeadCell from "@/components/table/TableHeadCell";
import TableRow from "@/components/table/TableRow";
import Text from "@/components/text/Text";
import type { ChildRouteContext } from "@/root";
import { throwIfHasNoPermission } from "@/utils/permissions";

type ChartType = "official" | "operational";

export const Route = createFileRoute("/admin/_panel/_chart/chart/")({
  beforeLoad: ({ context }) => {
    throwIfHasNoPermission(context.user, "chart.chart.view");

    return {
      crumb: [
        {
          label: context.t("Define Organizational Structure", {
            ns: "chart.admin",
          }),
        },
      ],
    } satisfies ChildRouteContext;
  },
  component: function ChartComponent() {
    const { t } = useTranslation("chart.shared");

    const data: {
      id: number;
      title: string;
      description: string;
      type: ChartType;
    }[] = [
      {
        id: 0,
        title: t("type.operational"),
        description: t(
          "All processes, workflows, and roles will proceed based on the units and positions defined in this structure.",
          { ns: "chart.admin" },
        ),
        type: "operational",
      },
      {
        id: 1,
        title: t("type.official"),
        description: t("This structure is for viewing purposes only.", {
          ns: "chart.admin",
        }),
        type: "official",
      },
    ];

    return (
      <div>
        <Container
          title={t("Organization chart")}
          description={t("Manage the organization chart", {
            ns: "chart.admin",
          })}
          className="flex h-full flex-col"
        >
          <Table
            className="w-full"
            header={t("Available Organizational Structures", {
              ns: "chart.admin",
            })}
          >
            <TableHead>
              <TableHeadCell>{t("Title", { ns: "core.shared" })}</TableHeadCell>
            </TableHead>
            {data?.map((item, index) => (
              <TableRow key={index}>
                <TableCell className="flex items-center justify-between px-6 py-4">
                  <div className="flex flex-col">
                    <Text size="sm" weight="semibold">
                      {" "}
                      {item.title}
                    </Text>
                    <Text size="sm" weight="light">
                      {" "}
                      {item.description}
                    </Text>
                  </div>
                  <Button
                    color="link-color"
                    link={{
                      to: "/chart/charts/$type",
                      target: "_blank",
                      params: { type: item.type },
                    }}
                  >
                    {t("View", { ns: "core.shared" })}
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </Table>
        </Container>
      </div>
    );
  },
});
